import { useState } from "react";
import { Transforms } from "slate";
import { ReactEditor, useSlateStatic } from "slate-react";
import { CustomElement } from "../../../../../slate";
import { useReorderItemsByIndex } from "../../../primitives/ReorderItems";
import { generateCode } from "../common";

export interface GridItemData {
    id: string;
    content: any;
    colSpan: number;
    rowSpan: number;
}

interface GridNodeData {
    columns: number;
    grid_items: GridItemData[];
}

export const useGrid = (element: CustomElement) => {
    const editor = useSlateStatic();
    const path = ReactEditor.findPath(editor, element);

    // Initial State extraction
    const initialColumns = (element as any).columns as number || 3;
    const initialItems = (element as any).grid_items as GridItemData[] || [];

    const [localState, setLocalState] = useState<GridNodeData>({
        columns: initialColumns,
        grid_items: initialItems,
    });

    // Helper to commit changes to Slate
    const commitToSlate = (newState: GridNodeData) => {
        setLocalState(newState);
        Transforms.setNodes(
            editor,
            newState as any,
            { at: path },
        );
    };

    // 1. Update Layout (Number of Columns)
    const updateColumnsNumber = (n: number) => {
        if (n < 1) return;
        commitToSlate({ ...localState, columns: n });
    };

    // 2. Add new Item
    const addItem = () => {
        const newItem: GridItemData = {
            id: generateCode(),
            content: {},
            colSpan: 1,
            rowSpan: 1
        };
        commitToSlate({
            ...localState,
            grid_items: [...localState.grid_items, newItem]
        });
    };

    // 3. Update Content of a specific item
    const updateItemContent = (index: number, newContent: any) => {
        const newItems = [...localState.grid_items];
        newItems[index] = { ...newItems[index], content: newContent };
        // We generally don't full commit on every keystroke if performance is an issue, 
        // but for structure it's safer. Optimization depends on your EditorComponent.
        commitToSlate({ ...localState, grid_items: newItems });
    };

    // 4. Update Spans (Row/Col)
    const updateItemSpan = (index: number, field: 'colSpan' | 'rowSpan', value: number) => {
        const val = Math.max(1, value); // Minimum 1
        const newItems = [...localState.grid_items];
        newItems[index] = { ...newItems[index], [field]: val };
        commitToSlate({ ...localState, grid_items: newItems });
    };

    // 5. Delete Item
    const deleteItem = (index: number) => {
        const newItems = localState.grid_items.filter((_, i) => i !== index);
        commitToSlate({ ...localState, grid_items: newItems });
    };

    // 6. Reorder Logic
    const reorderLogic = useReorderItemsByIndex<GridItemData>({
        startWith: undefined, // Dialog isn't open by default
        updateItems: async (items) => {
            // This is called when "Save" is clicked in the dialog
            commitToSlate({ ...localState, grid_items: items });
            return Promise.resolve();
        }
    });

    return {
        ...localState,
        updateColumnsNumber,
        addItem,
        updateItemContent,
        updateItemSpan,
        deleteItem,
        reorderLogic
    };
}
